import React, { useState, useEffect } from 'react';
import Navbar from '../Shared/Navbar';
import api from '../../config/api';
import './PipelineManager.css';

function PipelineManager({ user, onLogout }) {
  const [candidates, setCandidates] = useState([]);
  const [selectedStage, setSelectedStage] = useState('all');
  const [loading, setLoading] = useState(true);

  const stages = [
    { id: 'all', name: 'All Candidates' },
    { id: 'new', name: 'New Lead' },
    { id: 'review', name: 'Portfolio Review' },
    { id: 'interviewing', name: 'Interviewing' },
    { id: 'assessment', name: 'Skills Assessment' },
    { id: 'offer', name: 'Offer Stage' },
    { id: 'hired', name: 'Hired' }
  ];

  useEffect(() => {
    fetchCandidates();
  }, []);

  const fetchCandidates = async () => {
    try {
      const response = await api.get('/api/pipeline/default/candidates');
      setCandidates(response.data.candidates);
    } catch (error) {
      console.error('Failed to fetch candidates:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = async (candidateId, newStage) => {
    try {
      await api.put(`/api/pipeline/candidates/${candidateId}/stage`,
        { stage: newStage }
      );
      
      setCandidates(candidates.map(c => 
        c.id === candidateId ? { ...c, stage: newStage } : c
      ));
    } catch (error) {
      console.error('Failed to update stage:', error);
    }
  };

  const filteredCandidates = selectedStage === 'all' 
    ? candidates 
    : candidates.filter(c => c.stage === selectedStage);

  if (loading) {
    return (
      <div>
        <Navbar user={user} onLogout={onLogout} userType="recruiter" />
        <div className="pipeline-container">
          <div className="loading">Loading pipeline...</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Navbar user={user} onLogout={onLogout} userType="recruiter" />
      
      <div className="pipeline-container">
        <div className="pipeline-header">
          <div>
            <h1>Pipeline Manager</h1>
            <p className="text-gray-600">Manage candidates through your hiring stages</p>
          </div>
          <div className="badge badge-info">
            {candidates.length} candidates
          </div>
        </div>

        {/* Stage Filter */}
        <div className="stage-filter card">
          {stages.map(stage => (
            <button
              key={stage.id}
              className={`stage-btn ${selectedStage === stage.id ? 'active' : ''}`}
              onClick={() => setSelectedStage(stage.id)}
            >
              {stage.name}
              {stage.id !== 'all' && (
                <span className="stage-count">
                  {candidates.filter(c => c.stage === stage.id).length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Kanban Board */}
        <div className="kanban-board">
          {stages.filter(s => s.id !== 'all').map(stage => {
            const stageCandidates = candidates.filter(c => c.stage === stage.id);
            
            return (
              <div key={stage.id} className="kanban-column">
                <div className="column-header">
                  <h3>{stage.name}</h3>
                  <span className="column-count">{stageCandidates.length}</span>
                </div>
                
                <div className="column-content">
                  {stageCandidates.map(candidate => (
                    <div key={candidate.id} className="candidate-card card">
                      <div className="candidate-header">
                        <img
                          src={candidate.profileImage}
                          alt={candidate.name}
                          className="candidate-avatar"
                        />
                        <div>
                          <h4>{candidate.name}</h4>
                          <p className="candidate-title">{candidate.title}</p>
                        </div>
                      </div>

                      {candidate.notes && (
                        <p className="candidate-notes">{candidate.notes}</p>
                      )}

                      {candidate.scorecard && Object.keys(candidate.scorecard).length > 0 && (
                        <div className="scorecard">
                          {Object.entries(candidate.scorecard).map(([key, value]) => (
                            <div key={key} className="scorecard-item">
                              <span className="scorecard-label">{key}</span>
                              <span className="scorecard-value">{value}/10</span>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="candidate-actions">
                        <select
                          className="input input-sm"
                          value={candidate.stage}
                          onChange={(e) => handleStageChange(candidate.id, e.target.value)}
                        >
                          {stages.filter(s => s.id !== 'all').map(s => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                  ))}
                  
                  {stageCandidates.length === 0 && (
                    <div className="empty-column">
                      No candidates in this stage
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default PipelineManager;
